import { COMPLETED, FOLLOW_UP, IN_PROGRESS, INITIAL } from "@app/constants/Constants";
import Config from "@app/constants/Config";
import { getDayDiff } from "@app/utils/helper";

function getUniqueItemsWithLatestFollowupDate(items) {
	const grouped = {};

	for (const item of items) {
		const key = `${item.values.kundenundeinsatzort_einsatzort_adressetyp}|${item.values.kundenundeinsatzort_kunde_kunde__ID}`;

		if (!grouped[key] || new Date(item.followupDate) > new Date(grouped[key].followupDate)) {
			grouped[key] = item;
		}
	}

	return Object.values(grouped);
}

function getAccidentDate(customers = [], inspection) {
	const { companyId, values } = inspection;
	const job = customers
		.find(({ KUNDENNR }) => KUNDENNR.toString() === companyId)
		?.EinsatzAdressen?.find(({ Adresse }) => Adresse === values.kundenundeinsatzort_einsatzort_adressetyp)
		?.Berufe?.find(({ Berufschluessel }) => Berufschluessel === values.taetigkeit_beschreibung_ueberlassungals__ID);
	return job?.UnfalldAtum ? new Date(job.UnfalldAtum) : null;
}

function getAccidentInspectionRequired(inspection, office) {
	const { visitDate } = inspection;

	const accidentDate = getAccidentDate(office?.kundenliste, inspection);
	const accidentDays = !!accidentDate && getDayDiff(accidentDate);

	return !!accidentDate && accidentDate >= new Date(visitDate) && accidentDays <= Config.ACCIDENT_REMINDER_OFFSET;
}

export function inspectionsByFilter(inspections, filterKey, office) {
	switch (filterKey) {
		case "initial-in-progress":
			return inspections.filter(({ status, type }) => type === INITIAL && status === IN_PROGRESS);

		case "upcoming": {
			// remove all inspections w/o followupDate or incomplete
			let res = inspections.filter(({ status, followupDate }) => !!followupDate && status === COMPLETED);
			// sort all inspections by followupDate
			res = res.sort((a, b) => a.followupDate - b.followupDate);
			// filter items that are unique by values.kundenundeinsatzort_einsatzort_adressetyp and values.kundenundeinsatzort_kunde_kunde__ID and keep, the ones with the latest followupDate
			res = getUniqueItemsWithLatestFollowupDate(res);
			// filter inspections within followUpReminder timespan OR within accident timespan
			res = res.filter((inspection) => {
				const { followupDate } = inspection;
				const followUpDays = getDayDiff(followupDate);

				return (
					followUpDays <= Config.FOLLOWUP_REMINDER_OFFSET || // case regular follow up
					getAccidentInspectionRequired(inspection, office) // case accident
				);
			});
			return res;
		}

		case "follow-up-in-progress":
			return inspections.filter(({ status, type }) => type === FOLLOW_UP && status === IN_PROGRESS);

		// case "follow-up-completed":
		//   return inspections
		//     .filter(({ status, type }) => type === FOLLOW_UP && status === COMPLETED);

		case "unsynchronized":
			// return inspections.filter(({ synchronized }) => synchronized !== undefined && synchronized === false);
			return inspections.filter(({ meta }) => meta.synced !== true);

		case "archive":
			return inspections.filter(({ status }) => status === COMPLETED);

		default:
			return inspections;
	}
}

export function setUpcomingCategory(inspections, office) {
	return inspections.map((inspection) => {
		const { followupDate } = inspection;
		const followUpDays = getDayDiff(followupDate);

		let category;
		if (getAccidentInspectionRequired(inspection, office)) {
			category = "accident";
		} else if (followUpDays <= Config.FOLLOWUP_REMINDER_URGENT_OFFSET) {
			category = "dueUrgent";
		} else if (followUpDays <= Config.FOLLOWUP_REMINDER_SOON_OFFSET) {
			category = "dueSoon";
		}
		return { ...inspection, upcomingCategory: category };
	});
}

export function hasCompletedFollowUp(inspections, followUpUuid) {
	return !!inspections.find(({ uuid }) => uuid === followUpUuid);
}

// Firmenname-Anzeige für eine besichtigung, aufgelöst über die kundenliste der
// niederlassung: 1) aktiver kunde mit der hinterlegten kunde-nummer, 2)
// nachfolgender kunde, der die nummer als ehemalige überführt hat (niederlassungs-
// bzw. kundenumzug), 3) fallback auf die hinterlegten company_name/company_id.
// inaktiv-Check über das Status-String-Feld der kundenliste (z.b. "inaktiv"),
// konsistent mit dem firmenname-dropdown (FormStepContainer). isCustomerInactive
// prüft dagegen das numerische customer_state-Feld ("0") und passt hier nicht.
const isStatusInactive = (Status) =>
	!!Status && String(Status).trim().toLowerCase() === "inaktiv";

export function getCompanyDisplay(inspection, office) {
	const { companyId, companyName } = inspection || {};
	if (companyId === undefined || companyId === null || companyId === "") {
		return { name: undefined, id: undefined };
	}
	const customers = office?.kundenliste || [];
	const active = customers.find(
		({ KUNDENNR, Status }) => KUNDENNR?.toString() === String(companyId) && !isStatusInactive(Status),
	);
	if (active) {
		return { name: active.KundenName, id: active.KUNDENNR };
	}
	const successor = customers.find(({ Status, Ehemalige_Kunden_Nummern }) => {
		if (isStatusInactive(Status)) {
			return false;
		}
		return (Ehemalige_Kunden_Nummern || []).some((nr) => String(nr) === String(companyId));
	});
	if (successor) {
		return { name: successor.KundenName, id: successor.KUNDENNR };
	}
	return { name: companyName, id: companyId };
}

export function getFormerlyCompanyId(value) {
	if (value === null || value === undefined) {
		return undefined;
	}

	let list = value;
	if (typeof list === "string") {
		const trimmed = list.trim();
		if (!trimmed) {
			return undefined;
		}
		try {
			list = JSON.parse(trimmed);
		} catch (e) {
			list = trimmed.split(",");
		}
	}

	const first = Array.isArray(list) ? list[0] : list;
	const id = String(first ?? "").replace(/["[\]\s]/g, "");
	return /^[1-9]\d*$/.test(id) ? id : undefined;
}

export function isCustomerInactive(customerState) {
	if (customerState === null || customerState === undefined || customerState === "") {
		return false;
	}
	return String(customerState).trim() === "0";
}
